// QRCode.js

import { EncodeHintType } from '@zxing/library';
import { BrowserQRCodeSvgWriter } from '@zxing/library/esm/browser/BrowserQRCodeSvgWriter';

import { buildAbsoluteNodeUrl, NODE_BASE_PATH, PUBLIC_APP_ORIGIN } from 'utilities/Routing';
import { toFolderSlug } from 'utilities/Strings';

export const QR_CODE_SIZE = 220;

export const buildNodeQRCodeUrl = (nodeKey) => buildAbsoluteNodeUrl(nodeKey, PUBLIC_APP_ORIGIN);

export const buildNodeQRCodeSvgMarkup = (value, options) => buildQRCodeSvgMarkup(value, options);

export const renderQRCodeSvgToPngDataUrl = async (svgMarkup, { size = QR_CODE_SIZE } = {}) => {
  const content = String(svgMarkup || '').trim();
  if (!content) return '';
  if (typeof Blob === 'undefined' || typeof Image === 'undefined' || typeof URL === 'undefined') return '';
  if (typeof document === 'undefined') return '';

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d');
  if (!context) return '';

  const blob = new Blob([content], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new Image();

      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error('Failed to rasterize QR SVG.'));
      nextImage.src = objectUrl;
    });

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(image, 0, 0, size, size);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const buildNodeQRCodeTitle = (node) => {
  const title = String(node?.title || '').trim();
  return title || 'Untitled';
};

export const buildNodeQRCodeFilename = (node) => {
  const titleSlug = toFolderSlug(buildNodeQRCodeTitle(node));
  const keySlug = String(node?.key || '').trim();
  const suffix = keySlug || 'node';
  return `${titleSlug || 'qr_code'}_${suffix}_qr`;
};

export const buildQRCodeSvgMarkup = (value, { margin = 2, size = QR_CODE_SIZE } = {}) => {
  if (typeof document === 'undefined' || typeof XMLSerializer === 'undefined') return '';

  const text = String(value || '').trim();
  if (!text) return '';

  const hints = new Map();
  hints.set(EncodeHintType.MARGIN, margin);

  const writer = new BrowserQRCodeSvgWriter();
  const svgElement = writer.write(text, size, size, hints);

  svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svgElement.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svgElement.setAttribute('width', String(size));
  svgElement.setAttribute('height', String(size));
  svgElement.setAttribute('shape-rendering', 'crispEdges');

  return new XMLSerializer().serializeToString(svgElement);
};

export const downloadQRCodeSvg = ({ filename, svgMarkup } = {}) => {
  const content = String(svgMarkup || '').trim();
  if (!content || typeof document === 'undefined' || typeof URL === 'undefined') return false;

  const blob = new Blob([content], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = `${String(filename || 'qr_code').trim() || 'qr_code'}.svg`;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
  return true;
};

export const resolveNodeKeyFromQRCodeValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const directNodePathMatch = text.match(/^\/node\/([^/?#]+)$/i);
  if (directNodePathMatch) {
    return decodeURIComponent(directNodePathMatch[1] || '').trim();
  }

  try {
    const parsed = new URL(text);
    const pathname = String(parsed.pathname || '').trim();
    if (!pathname.startsWith(`${NODE_BASE_PATH}/`)) return '';

    const encodedNodeKey = pathname.slice(NODE_BASE_PATH.length + 1).split('/')[0] || '';
    return decodeURIComponent(encodedNodeKey).trim();
  } catch {
    return '';
  }
};